import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Grid,
  Box,
  TextField,
  IconButton,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate } from "react-router-dom";
import Pedido, {
  estadoInicialFiltroPesquisarPedido as estadoInicialFiltroPesquisarOrcamento,
  FiltroPesquisarPedido,
} from "../Shared/Interfaces/Pedido";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import React, { useContext, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import ImprimirOrcamento from "./ImprimirOrcamento";
import {
  formatDateToDDMMYYYY,
  formatToBRL,
} from "../../Utils/utils";
import { AppProvider } from "../../ContextApi/AppProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import DefaultLoader from "../Shared/DefaultLoader";
import { OrcamentoService } from "../../Services/OrcamentoService";
import colors from "../../theme/colors";

export default function ConsultarOrcamentos() {
  const navigate = useNavigate();
  const contextApi = useContext(AppProvider);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dataInicial, setDataInicial] = useState<Dayjs>(dayjs().subtract(30, 'day'));
  const [dataFinal, setDataFinal] = useState<Dayjs>(dayjs());
  const [filtroPesquisarOrcamento, setFiltroPesquisarOrcamento] =
    React.useState<FiltroPesquisarPedido>(estadoInicialFiltroPesquisarOrcamento);
  const [loading, setLoading] = useState(false);
  const handleInputPesquisarOrcamento = (
    fieldName: keyof FiltroPesquisarPedido,
    value: string | number
  ) => {
    if (fieldName == "idPedido" && value == "") {
      value = 0;
    }
    setFiltroPesquisarOrcamento((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const orcamentoService = new OrcamentoService();
  const [orcamentos, setOrcamentos] = React.useState<Pedido[]>([]);
  const handlePrint = (pedido: Pedido) => {
    openPDFInNewTab(pedido);
  };

  const handleDeleteItem = (orcamento: Pedido) => {
    orcamentoService
      .deleteItem(orcamento.id.toString())
      .then((response) => {
        if (response.status == 204) {
          contextApi.showMessage(
            "Orcamento excluído com sucesso!",
            true,
            "success"
          );
          const filtroPesquisa: FiltroPesquisarPedido = {
            idPedido: 0,
            nomeCliente: '',
            dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
            dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
          };
          PesquisarPor(filtroPesquisa);
        } else {
          contextApi.showMessage(
            "Erro ao excluir o Orçamento, tente novamente!",
            true,
            "error"
          );
        }
      })
      .catch((error) => {
        contextApi.showMessage(error.response.data, true, "error");
      });
  };

  const openPDFInNewTab = async (pedido: Pedido) => {
    const blob = await pdf(
      <ImprimirOrcamento data={pedido} numeroPedido={`#000${pedido.id}`} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const PesquisarPor = (filtroPesquisarPedido: FiltroPesquisarPedido) => {
    setLoading(true);
    setOrcamentos([]); // Clear the current pedidos state before fetching new data
    const filtroPesquisa: FiltroPesquisarPedido = {
       idPedido: filtroPesquisarPedido.idPedido,
       nomeCliente: filtroPesquisarPedido.nomeCliente,
       dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
       dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
     };

    orcamentoService
      .buscarPor(filtroPesquisa)
      .then((response) => {
        if (response.status === 200) {
          setOrcamentos(response.data); // Updates the pedidos state with the response data
          setFiltroPesquisarOrcamento(estadoInicialFiltroPesquisarOrcamento);
          setLoading(false);
        }
      })
      .catch((error) => {
        const errorMessage = error.response?.data || "An error occurred"; // Handle potential undefined error responses
        contextApi.showMessage(errorMessage, true, "error");
        setLoading(false);
      });
  };

  const handleEditOrcamento = (row: Pedido) => {
    navigate(`/app/gerarorcamentos/${row.id}`);
  };

  React.useEffect(() => {
    const filtroPesquisa: FiltroPesquisarPedido = {
      idPedido: 0,
      nomeCliente: '',
      dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
      dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
    };
    PesquisarPor(filtroPesquisa);
  }, []); // Empty array ensures this runs only once when the component mounts

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Cabeçalho e Descrição */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
              <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                📋 Consultar Orçamentos
              </h4>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Pesquisa e visualização de orçamentos existentes
              </Typography>
            </Paper>
          </Grid>

          {/* Linha 2 - Filtros de Pesquisa */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>🔍 Filtros de Pesquisa</h4>
              
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Grid container spacing={2} alignItems="center">
                  {/* Filtro ID Orçamento */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      name="id"
                      label="ID Orçamento"
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={filtroPesquisarOrcamento.idPedido}
                      onChange={(e) =>
                        handleInputPesquisarOrcamento("idPedido", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Filtro Nome Cliente */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      name="nome"
                      label="Cliente"
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={filtroPesquisarOrcamento.nomeCliente}
                      onChange={(e) =>
                        handleInputPesquisarOrcamento("nomeCliente", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Filtro Data Inicial */}
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="Data Inicial"
                      value={dataInicial}
                      format="DD/MM/YYYY"
                      onChange={(newValue) =>
                        setDataInicial(newValue ?? dataInicial)
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true
                        }
                      }}
                    />
                  </Grid>

                  {/* Filtro Data Final */}
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="Data Final"
                      value={dataFinal}
                      format="DD/MM/YYYY"
                      onChange={(newValue) =>
                        setDataFinal(newValue ?? dataFinal)
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true
                        }
                      }}
                    />
                  </Grid>

                  {/* Botão Pesquisar */}
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={loading}
                      sx={{ height: '40px' }}
                      onClick={() => PesquisarPor(filtroPesquisarOrcamento)}
                    >
                      {loading ? (
                        <DefaultLoader
                          height="2.5em"
                          width="2.5em"
                          colors={["#1876D2", "#1876D2", "#1876D2", "#1876D2", "#1876D2"]}
                          textInfo="Consultando Orçamentos"
                        />
                      ) : (
                        "Pesquisar"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </Paper>
          </Grid>

          {/* Linha 3 - Tabela de Orçamentos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📋 Lista de Orçamentos</h4>
              
              <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                {orcamentos.length > 0 ? (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell>Valor Total</TableCell>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Itens do Orçamento</TableCell>
                            <TableCell>Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orcamentos.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>{row.id}</TableCell>
                              <TableCell>{formatDateToDDMMYYYY(row.dataCriacao)}</TableCell>
                              <TableCell>{formatToBRL(row.valorTotal)}</TableCell>

                              <TableCell>
                                <Typography variant="body2"><strong>Nome:</strong> {row.cliente?.nome}</Typography>
                                <Typography variant="body2"><strong>Documento:</strong> {row.cliente?.documento}</Typography>
                                <Typography variant="body2"><strong>Telefone:</strong> {row.cliente?.telefone}</Typography>
                                <Typography variant="body2"><strong>Email:</strong> {row.cliente?.email}</Typography>
                                <Typography variant="body2">
                                  <strong>Endereço:</strong> {row.cliente?.cep}, {row.cliente?.logradouro}, {row.cliente?.numero}, {row.cliente?.complemento},{" "}
                                  {row.cliente?.bairro}, {row.cliente?.cidade} - {row.cliente?.estado}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                {row.itensPedido?.map((item) => (
                                  <Box key={item.idProduto} sx={{ mb: 1 }}>
                                    <Typography variant="body2"><strong>Produto:</strong> {item.descricao}</Typography>
                                    <Typography variant="body2"><strong>Quantidade:</strong> {item.quantidade}</Typography>
                                    <Typography variant="body2"><strong>Preço:</strong> {formatToBRL(item.preco)}</Typography>
                                  </Box>
                                ))}
                              </TableCell>

                              <TableCell>
                                <Grid container spacing={1}>
                                  <Grid item>
                                    <IconButton color="primary" onClick={() => handlePrint(row)}>
                                      <PrintIcon />
                                    </IconButton>
                                  </Grid>
                                  <Grid item>
                                    <IconButton color="primary" onClick={() => handleEditOrcamento(row)}>
                                      <EditIcon />
                                    </IconButton>
                                  </Grid>
                                  <Grid item>
                                    <IconButton color="primary" onClick={() => handleDeleteItem(row)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <TablePagination
                      rowsPerPageOptions={[10]}
                      component="div"
                      count={orcamentos.length ?? 0}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Nenhum orçamento encontrado para os filtros selecionados
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
  
}
